/**
 * Effect and Parameter Descriptions
 * 
 * Provides help text and tooltips for all effects and their parameters.
 * Used throughout the UI to provide context-sensitive help.
 */

import type { EffectType } from '../types/effects';

/**
 * Effect descriptions with detailed explanations
 */
export const EFFECT_DESCRIPTIONS: Record<EffectType, {
  description: string;
  usage: string;
  parameters: Record<string, string>;
}> = {
  bitcrusher: {
    description: 'Lo-fi digital distortion effect that reduces sample rate and bit depth',
    usage: 'Use to create retro, crunchy, or degraded sounds reminiscent of early digital audio',
    parameters: {
      sampleRate: 'Controls the sample rate reduction. Lower values create more aliasing and digital artifacts',
      bitDepth: 'Controls the bit depth reduction. Lower values create more quantization noise and distortion',
    },
  },
  
  reverb: {
    description: 'Algorithmic reverb that simulates acoustic spaces and adds depth',
    usage: 'Use to add spatial depth, simulate rooms, or create ambient textures',
    parameters: {
      roomSize: 'Controls the size of the simulated space. Larger values create longer decay times',
      damping: 'Controls high-frequency absorption. Higher values create darker, more muffled reverb',
      preDelay: 'Time before reverb onset. Simulates distance to walls and adds clarity',
      mix: 'Balance between dry (original) and wet (reverberated) signal',
    },
  },
  
  delay: {
    description: 'Echo effect that creates rhythmic repeats of the input signal',
    usage: 'Use to create echoes, rhythmic patterns, or add depth and space',
    parameters: {
      delayTime: 'Time between echoes. Can be synced to tempo for musical timing',
      feedback: 'Amount of signal fed back. Higher values create more repeats',
      filterFreq: 'Tone control for delayed signal. Lower values create darker echoes',
      mix: 'Balance between dry (original) and wet (delayed) signal',
    },
  },
  
  overdrive: {
    description: 'Warm analog-style distortion with harmonic saturation',
    usage: 'Use to add warmth, grit, or aggressive distortion to sounds',
    parameters: {
      drive: 'Amount of distortion applied. Higher values create more harmonics',
      tone: 'Frequency response control. Lower values emphasize bass, higher values emphasize treble',
      outputLevel: 'Output gain compensation. Adjust to match volume after distortion',
    },
  },
  
  // DISABLED: Chorus has audio artifacts
  // chorus: {
  //   description: 'Modulation effect that creates width and thickness through pitch variation',
  //   usage: 'Use to thicken sounds, create stereo width, or add subtle movement',
  //   parameters: {
  //     rate: 'Speed of the modulation. Lower values create slow sweeps, higher values create vibrato',
  //     depth: 'Intensity of pitch modulation. Higher values create more pronounced effect',
  //     delay: 'Base delay time. Affects the character of the chorus effect',
  //     mix: 'Balance between dry (original) and wet (chorused) signal',
  //   },
  // },
  
  eq: {
    description: '3-band parametric equalizer for precise frequency shaping',
    usage: 'Use to boost or cut specific frequencies, fix problems, or shape tone',
    parameters: {
      lowFreq: 'Center frequency for low band. Typically used for bass frequencies',
      lowGain: 'Boost or cut amount for low band. Positive values boost, negative values cut',
      lowQ: 'Bandwidth for low band. Higher values create narrower, more focused adjustments',
      midFreq: 'Center frequency for mid band. Typically used for body and presence',
      midGain: 'Boost or cut amount for mid band. Positive values boost, negative values cut',
      midQ: 'Bandwidth for mid band. Higher values create narrower, more focused adjustments',
      highFreq: 'Center frequency for high band. Typically used for treble and air',
      highGain: 'Boost or cut amount for high band. Positive values boost, negative values cut',
      highQ: 'Bandwidth for high band. Higher values create narrower, more focused adjustments',
    },
  },
  
  compressor: {
    description: 'Dynamics processor that reduces dynamic range by attenuating signals above a threshold',
    usage: 'Use to control dynamics, add punch and sustain, achieve a more polished sound, or create parallel compression effects',
    parameters: {
      threshold: 'Level above which compression is applied. Lower values compress more of the signal',
      ratio: 'Amount of gain reduction applied to signals above threshold. Higher ratios create more aggressive compression',
      attack: 'Time for compressor to respond to signals exceeding threshold. Fast attack catches transients, slow attack preserves punch',
      release: 'Time for compressor to stop attenuating after signal drops below threshold. Affects sustain and pumping character',
      makeup: 'Output level boost to compensate for gain reduction. Use to match volume after compression',
      mix: 'Balance between dry and compressed signal. Lower values create parallel compression for natural dynamics with added punch',
    },
  },

  flanger: {
    description: 'Modulation effect that creates sweeping, metallic, jet-like sounds by mixing signal with modulated delayed copy',
    usage: 'Use to create dramatic sweeping textures, add movement and width, or achieve psychedelic and sci-fi effects',
    parameters: {
      rate: 'Speed of the LFO modulation. Lower values create slow sweeps, higher values create rapid warbling',
      depth: 'Intensity of delay time modulation. Higher values create more pronounced sweeping and comb filtering',
      delay: 'Base delay time before modulation. Affects the character and frequency range of the flanging effect',
      feedback: 'Amount of processed signal fed back into input. Positive values intensify effect, negative values create through-zero flanging',
      mix: 'Balance between dry and flanged signal. 50% typically provides classic flanging sound',
    },
  },

  tremolo: {
    description: 'Amplitude modulation effect that creates rhythmic volume pulsing by varying signal level at specified rate',
    usage: 'Use to create rhythmic pulsing, vintage amp vibes, add movement to sustained sounds, or create extreme chopping effects',
    parameters: {
      rate: 'Speed of amplitude modulation. Lower values create slow pulsing, higher values create rapid tremolo or ring modulation effects',
      depth: 'Intensity of volume modulation. Higher values create more pronounced pulsing, lower values create subtle movement',
      waveform: 'Shape of modulation: 0=Sine (smooth), 1=Triangle (linear), 2=Square (choppy). Affects character of pulsing',
      phase: 'Stereo phase offset for width. 0° creates mono tremolo, 90° creates alternating left-right pulsing, 180° creates opposing channels',
    },
  },

  pingpong: {
    description: 'Stereo delay effect where echoes alternate between left and right channels, creating bouncing spatial effect',
    usage: 'Use to create wide, bouncing echoes, add spatial interest to mixes, or create rhythmic stereo patterns',
    parameters: {
      delayTime: 'Time between echoes. Can be synced to tempo for musical timing. Shorter times create slapback, longer times create distinct echoes',
      feedback: 'Amount of signal fed back for repeats. Higher values create more echoes, but avoid values above 90% to prevent runaway',
      stereoWidth: 'Amount of left-right separation. 0% creates mono delay, 100% creates full ping-pong alternation between channels',
      filterFreq: 'Lowpass filter cutoff for delayed signal. Lower values create darker, more muffled echoes that sit back in mix',
      mix: 'Balance between dry and delayed signal. Lower values keep effect subtle, higher values make echoes more prominent',
    },
  },

  autowah: {
    description: 'Envelope-following filter that creates dynamic, vocal-like sweeps by modulating resonant filter based on input amplitude',
    usage: 'Use to create funky, expressive filter sweeps, add dynamic movement that responds to playing, or achieve talking instrument effects',
    parameters: {
      sensitivity: 'How much the filter responds to input level. Higher values create more dramatic sweeps from quiet to loud signals',
      minFreq: 'Minimum filter cutoff frequency at low input levels. Sets the bottom of the sweep range',
      maxFreq: 'Maximum filter cutoff frequency at high input levels. Sets the top of the sweep range',
      resonance: 'Filter emphasis at cutoff frequency. Higher values create more pronounced, vocal-like character but can become harsh',
      attack: 'Envelope follower attack time. Fast attack makes filter respond quickly to transients, slow attack creates smoother sweeps',
      release: 'Envelope follower release time. Fast release makes filter close quickly, slow release creates sustained sweeps',
      mix: 'Balance between dry and filtered signal. 100% typically provides full auto-wah effect',
    },
  },

  none: {
    description: 'Empty effect slot',
    usage: 'Click "Add Effect" to select an effect for this slot',
    parameters: {},
  },
};

/**
 * Get description for an effect
 */
export function getEffectDescription(effectType: EffectType): string {
  return EFFECT_DESCRIPTIONS[effectType]?.description || 'No description available';
}

/**
 * Get usage tip for an effect
 */
export function getEffectUsage(effectType: EffectType): string {
  return EFFECT_DESCRIPTIONS[effectType]?.usage || '';
}

/**
 * Get description for a parameter
 */
export function getParameterDescription(effectType: EffectType, parameterId: string): string {
  return EFFECT_DESCRIPTIONS[effectType]?.parameters[parameterId] || '';
}

/**
 * Check if an effect has parameter descriptions
 */
export function hasParameterDescriptions(effectType: EffectType): boolean {
  const params = EFFECT_DESCRIPTIONS[effectType]?.parameters;
  return params ? Object.keys(params).length > 0 : false;
}
